import { Caption1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import {
  ClipboardTextLtr16Regular,
  Document16Regular,
  GanttChart16Regular,
  LockClosed16Regular,
  MoreHorizontal16Regular,
  Note16Regular,
  People16Regular,
  TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import type { ReactNode } from "react";
import { FuiButton } from "@/components/fui-control";
import type { DocumentKind } from "@/engine/types";
import { RecordStatusBadge } from "@/features/records/record-ui";
import { StatusBadge } from "@/features/tasks/task-ui";
import type { DocumentChip } from "./doc-links";

const useStyles = makeStyles({
  chip: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    width: "100%",
    padding: "4px 6px",
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFaster,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":hover .chipMore, :focus-within .chipMore": { opacity: 1 },
  },
  open: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    flexGrow: 1,
    padding: "2px 4px",
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    cursor: "pointer",
    borderRadius: tokens.borderRadiusSmall,
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
    ":disabled": { cursor: "default" },
  },
  code: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: "tabular-nums",
  },
  title: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  missing: { color: tokens.colorNeutralForeground3 },
  meta: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flexGrow: 1,
  },
  supersede: { color: tokens.colorNeutralForeground3 },
  icon: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  moreWrap: {
    opacity: 0,
    transitionProperty: "opacity",
    transitionDuration: tokens.durationFaster,
    flexShrink: 0,
  },
});

export function DocKindIcon({ kind, className }: { kind: DocumentKind; className?: string }) {
  const styles = useStyles();
  const cls = mergeClasses(styles.icon, className);
  if (kind === "meeting") return <People16Regular className={cls} />;
  if (kind === "material") return <Document16Regular className={cls} />;
  return <Note16Regular className={cls} />;
}

function KindIcon({ chip }: { chip: DocumentChip }) {
  const styles = useStyles();
  if (chip.targetKind === "record") return <ClipboardTextLtr16Regular className={styles.icon} />;
  if (chip.targetKind === "scheduleTask") return <GanttChart16Regular className={styles.icon} />;
  return <TaskListSquareLtr16Regular className={styles.icon} />;
}

function Status({ chip }: { chip: DocumentChip }) {
  const styles = useStyles();
  if (chip.missing) return <Caption1 className={styles.missing}>삭제됨</Caption1>;
  if (chip.recordKind && chip.recordStatus) return <RecordStatusBadge kind={chip.recordKind} status={chip.recordStatus} />;
  if (chip.targetKind === "executionTask") return <StatusBadge status={chip.executionStatus} size="small" />;
  if (chip.status) return <Caption1 className={styles.missing}>{chip.status}</Caption1>;
  return null;
}

export function DocLinkChip({
  chip,
  onOpen,
  onUnlink,
}: {
  chip: DocumentChip;
  onOpen?: () => void;
  onUnlink?: () => void;
}) {
  const styles = useStyles();
  const label = [chip.code, chip.title].filter(Boolean).join(" ");
  return (
    <div className={styles.chip}>
      <KindIcon chip={chip} />
      <button type="button" className={styles.open} onClick={onOpen} disabled={!onOpen}>
        <span className={styles.meta}>
          <span className={styles.title}>
            {chip.code ? <span className={styles.code}>{chip.code} </span> : null}
            <Tooltip content={chip.missing ? `${label} · 삭제된 항목입니다` : label} relationship="description">
              <span className={mergeClasses(styles.title, chip.missing && styles.missing)}>{chip.title}</span>
            </Tooltip>
          </span>
          {chip.supersede ? <Caption1 className={styles.supersede}>{chip.supersede}</Caption1> : null}
        </span>
      </button>
      <Status chip={chip} />
      {onUnlink ? (
        <span className={mergeClasses(styles.moreWrap, "chipMore")}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label={`${label} 동작`} />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={onUnlink}>연결 해제</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </span>
      ) : null}
    </div>
  );
}

const useMarkStyles = makeStyles({
  mark: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    maxWidth: "168px",
    height: "22px",
    padding: "0 6px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase300,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground3Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  quiet: {
    cursor: "default",
    color: tokens.colorNeutralForeground3,
  },
  title: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  code: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: "tabular-nums",
  },
  icon: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  more: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
});

/** 목록 칸의 짧은 칩. 상태 이름은 툴팁에 두고, 삭제됨만 칩에 적는다. */
export function DocLinkMark({ chip, onOpen }: { chip: DocumentChip; onOpen?: () => void }) {
  const styles = useMarkStyles();
  const label = [chip.code, chip.title].filter(Boolean).join(" ");
  const tip = chip.missing ? `${label} · 삭제된 항목입니다` : [label, chip.status].filter(Boolean).join(" · ");
  return (
    <Tooltip content={tip} relationship="description">
      <button
        type="button"
        className={mergeClasses(styles.mark, !onOpen && styles.quiet)}
        aria-disabled={!onOpen}
        aria-label={tip}
        onClick={(event) => {
          event.stopPropagation();
          onOpen?.();
        }}
      >
        <KindIcon chip={chip} />
        {chip.code ? <span className={styles.code}>{chip.code}</span> : null}
        <span className={styles.title}>{chip.title}</span>
        {chip.missing ? <span className={styles.more}>삭제됨</span> : null}
      </button>
    </Tooltip>
  );
}

export function DocLinkMore({ count, titles }: { count: number; titles: string }) {
  const styles = useMarkStyles();
  if (count <= 0) return null;
  return (
    <Tooltip content={titles} relationship="description">
      <span className={styles.more}>{`+${count}`}</span>
    </Tooltip>
  );
}

export function DocVisibilityChoices({
  value,
  privateBody,
  planBody,
  localNote,
  disabled,
  privateDisabled,
  onPrivate,
  onPlan,
}: {
  value: "private" | "plan";
  privateBody: string;
  planBody: string;
  localNote?: string;
  disabled?: boolean;
  privateDisabled?: boolean;
  onPrivate: () => void;
  onPlan: () => void;
}) {
  const styles = useVisibilityStyles();
  return (
    <div className={styles.wrap}>
      <div className={styles.row} role="group" aria-label="구분">
        <Choice on={value === "plan"} disabled={disabled} onClick={onPlan}>
          <People16Regular />
          <span>공개</span>
        </Choice>
        <Choice on={value === "private"} disabled={disabled || privateDisabled} onClick={onPrivate}>
          <LockClosed16Regular />
          <span>비공개</span>
        </Choice>
      </div>
      <Caption1 className={styles.body}>{value === "private" ? privateBody : planBody}</Caption1>
      {localNote ? <Caption1 className={styles.note}>{localNote}</Caption1> : null}
    </div>
  );
}

function Choice({ on, disabled, onClick, children }: { on: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  const styles = useVisibilityStyles();
  return (
    <button type="button" className={mergeClasses(styles.choice, on && styles.choiceOn)} aria-pressed={on} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

const useVisibilityStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", gap: "6px" },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "2px",
    padding: "2px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  choice: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    borderRadius: tokens.borderRadiusSmall,
    padding: "4px 10px",
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: tokens.durationFaster,
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
    ":disabled": { opacity: 0.45, cursor: "default" },
  },
  choiceOn: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
  },
  body: { color: tokens.colorNeutralForeground2, lineHeight: tokens.lineHeightBase200 },
  note: { color: tokens.colorNeutralForeground3, lineHeight: tokens.lineHeightBase200 },
});
